import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckboxMultiSelect } from "@/components/custom/CheckboxMultiSelect";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  useCreateCurrentAssessmentMutation,
  useGetPatientAssessmentDetailQuery,
} from "../../infrastructure/api/patientsApi";
import { useParams } from "react-router-dom";
import {
  getMinimumFutureTimeInputValue,
  isPastOrCurrentDateTime,
  isSameLocalDate,
} from "@/lib/dateUtils";
import { TimeEntryField } from "./scheduling/TimeEntryField";
import { useDoctorAvailabilityCheck } from "./scheduling/doctorAvailability";
import {
  currentTimeEntry,
  timeEntryFrom24h,
  timeEntryToDate,
  type TimeEntry,
} from "./scheduling/timeEntry";

type Props = {
  open: boolean;
  onClose: () => void;
  assessmentId: string;
};

const DEFAULT_SCREENING_DURATION_MINUTES = 45;

const ScheduleScreeningDialog: React.FC<Props> = ({
  open,
  onClose,
  assessmentId,
}) => {
  const { t } = useTranslation("patients");
  const days = [
    t("scheduling.days.su"),
    t("scheduling.days.mo"),
    t("scheduling.days.tu"),
    t("scheduling.days.we"),
    t("scheduling.days.th"),
    t("scheduling.days.fr"),
    t("scheduling.days.sa"),
  ];
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(
    new Date(today.getFullYear(), today.getMonth()),
  );
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [doctors, setDoctors] = useState<string[]>([]);
  const [doctorError, setDoctorError] = useState("");
  const [time, setTime] = useState<TimeEntry>(currentTimeEntry);

  const { patientId } = useParams<{ patientId: string }>();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();

  const { data, isLoading } = useGetPatientAssessmentDetailQuery(
    { patientId: patientId as string, assessmentId },
    { skip: !patientId || !assessmentId },
  );

  const recommendedDoctors =
    data?.data?.assessment?.screening_recommended_doctors ??
    data?.data?.assessment?.clinical_context_recommended_doctors ??
    [];

  const [createAssessment] = useCreateCurrentAssessmentMutation();
  const { isSlotAvailable, isChecking } = useDoctorAvailabilityCheck();

  const isCurrentMonth =
    year === today.getFullYear() && month === today.getMonth();

  const handlePrevMonth = () => {
    if (isCurrentMonth) return;
    setCurrentDate(new Date(year, month - 1));
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1));
    setSelectedDate(null);
  };

  const handleSchedule = async () => {
    let hasError = false;

    if (doctors.length === 0) {
      setDoctorError(t("scheduling.selectDoctorError"));
      hasError = true;
    }

    if (!selectedDate) {
      toast.error(t("scheduling.selectDateError"));
      hasError = true;
    }

    if (hasError) return;

    const startDate = timeEntryToDate(year, month, selectedDate!, time);
    if (!startDate) {
      return toast.error(t("scheduling.invalidTimeError"));
    }

    if (isPastOrCurrentDateTime(startDate)) {
      return toast.error(t("scheduling.futureTimeError"));
    }

    // The API requires an end time; appointments get the default duration.
    const endDate = new Date(startDate.getTime() + DEFAULT_SCREENING_DURATION_MINUTES * 60_000);

    const available = await isSlotAvailable({
      doctorIds: doctors,
      start: startDate,
      end: endDate,
      excludeAssessmentId: assessmentId,
    });
    if (!available) {
      return toast.error(t("scheduling.doctorUnavailableError"));
    }

    try {
      await createAssessment({
        patientId: patientId as string,
        data: {
          status: "SCR_SCHEDULED",
          scheduled_start_at: startDate.toISOString(),
          scheduled_end_at: endDate.toISOString(),
          notes: "Scheduled from UI",
          recommended_screening_doctor_user_ids: doctors,
        },
      }).unwrap();

      toast.success(t("scheduleScreeningDialog.success"));
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(apiErrorMessage(err, t("scheduleScreeningDialog.error")));
    }
  };

  const renderCalendar = () => {
    const cells: React.ReactNode[] = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} />);
    }

    const todayDate =
      year === today.getFullYear() && month === today.getMonth()
        ? today.getDate()
        : null;

    for (let d = 1; d <= totalDays; d++) {
      const isSelected = selectedDate === d;
      const isToday = todayDate === d;
      const isPast = new Date(year, month, d).setHours(23, 59, 59) < Date.now();

      cells.push(
        <button
          key={d}
          onClick={() => {
            if (isPast) return;
            setSelectedDate(d);

            const selected = new Date(year, month, d);
            const start = timeEntryToDate(year, month, d, time);
            if (isSameLocalDate(selected) && start && isPastOrCurrentDateTime(start)) {
              setTime(timeEntryFrom24h(getMinimumFutureTimeInputValue()));
            }
          }}
          disabled={isPast}
          className={`h-10 rounded-md text-sm ${
            isPast
              ? "text-gray-300 cursor-not-allowed border-none"
              : isSelected
                ? "bg-primary text-white border border-primary cursor-pointer"
                : isToday
                  ? "border border-primary font-semibold cursor-pointer"
                  : "hover:bg-gray-100 cursor-pointer"
          }`}
        >
          {d}
        </button>,
      );
    }

    return cells;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl p-0 sm:max-w-[650px]">
        {/* Header */}
        <div className="shrink-0 border-b px-6 pt-5 pb-3">
          <h2 className="text-lg font-semibold">{t("scheduleScreeningDialog.title")}</h2>
          <p className="text-sm text-gray-500">
            {t("scheduleScreeningDialog.subtitle")}
          </p>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-4">
          {/* Calendar */}
          <div>
            <div className="flex justify-between mb-3">
              <p className="font-medium">
                {currentDate.toLocaleString("default", { month: "short" })}{" "}
                {year}
              </p>
              <div className="flex gap-2">
                <button onClick={handlePrevMonth} disabled={isCurrentMonth}>
                  <ChevronLeft
                    size={16}
                    className={
                      isCurrentMonth ? "text-gray-300" : "cursor-pointer"
                    }
                  />
                </button>
                <button onClick={handleNextMonth}>
                  <ChevronRight size={16} className="cursor-pointer" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 text-xs text-gray-500 text-center mb-2">
              {days.map((d) => (
                <span key={d}>{d}</span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 text-center">
              {renderCalendar()}
            </div>
          </div>

          <TimeEntryField id="schedule-screening-time" value={time} onChange={setTime} />

          {/* Doctor */}
          <div>
            <label className="text-sm font-medium mb-1 block">
              {t("scheduling.doctorName")} <span className="text-destructive-text">*</span>
            </label>
            <CheckboxMultiSelect
              id="schedule-screening-doctors"
              options={recommendedDoctors.map((doc) => ({
                id: doc.id,
                label: doc.name,
                detail: doc.designation,
              }))}
              value={doctors}
              onChange={(ids) => {
                setDoctors(ids);
                setDoctorError("");
              }}
              placeholder={t("scheduling.selectDoctor")}
              selectedText={(count) => t("scheduling.doctorsSelected", { count })}
              emptyText={t("scheduling.noDoctors")}
              loading={isLoading}
              loadingText={t("scheduling.loading")}
              invalid={!!doctorError}
              removeLabel={(name) => t("scheduling.removeDoctor", { name })}
              triggerClassName="h-12"
            />
            {doctorError && (
              <p className="text-xs text-destructive-text mt-1">
                {doctorError}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 justify-end gap-2 border-t px-6 py-4">
          <Button
            variant="outline"
            onClick={onClose}
            className="cursor-pointer px-3 hover:bg-white"
          >
            {t("scheduling.cancel")}
          </Button>
          <Button
            onClick={handleSchedule}
            loading={isChecking}
            className="cursor-pointer bg-primary px-3 text-white"
          >
            {t("scheduleScreeningDialog.schedule")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleScreeningDialog;
